import { hasAnyAddressPart, isValidUsAddress, validateAddress } from '@/utils/addressValidation';
import { recordedPatientSex } from '@/utils/patientSex';

import type {
  EnrollmentAddress,
  EnrollmentChecklistToggles,
  EnrollmentRecord
} from '@/types/enrollment';

/**
 * Candidate Patient Enrollment checklist rules. Pure: no React, no store, no fetch, so the
 * patient list can reuse it. See docs/CPE_ENROLLMENT_CHECKLIST.md for the rules table.
 */

export type ChecklistSectionKey =
  | 'demographics'
  | 'clinic'
  | 'insurance'
  | 'device'
  | 'clinical'
  | 'vendor';

export type ChecklistSectionStatus = 'complete' | 'incomplete' | 'not_required';

export interface ChecklistItem {
  key: string;
  label: string;
  required: boolean;
  complete: boolean;
  /** Why it is required (when missing) or why it is not required. */
  reason?: string;
  /** Display value when there is one. */
  value?: string;
}

export interface ChecklistSection {
  key: ChecklistSectionKey;
  label: string;
  status: ChecklistSectionStatus;
  /** Set when the whole section is not required. */
  reason?: string;
  missingCount: number;
  items: ChecklistItem[];
}

export interface ChecklistMissing {
  sectionKey: ChecklistSectionKey;
  itemKey: string;
  label: string;
}

export interface EnrollmentChecklistResult {
  sections: ChecklistSection[];
  missing: ChecklistMissing[];
  isComplete: boolean;
  /** Sections that are complete, out of the sections that are required. */
  completeSections: number;
  requiredSections: number;
  /** Effective "transmission available" value; `null` until auto-detected or chosen. */
  transmissionAvailable: { value: boolean | null; source: 'auto' | 'admin' | null };
  /** Effective billing for this patient. */
  billing: { splitBill: boolean; reason: string };
}

export type AccountBillingType = 'Split Bill' | 'Fee For Service' | 'Hybrid';

export interface EnrollmentDeviceInput {
  id: string;
  vendor?: string | null;
  vendorName?: string | null;
  type?: string | null;
  modelName?: string | null;
  modelNumber?: string | null;
  serialNumber?: string | null;
  lastTransmissionAt?: string | null;
  /** False when the only linked device is inactive. Omitted means the device counts as active. */
  active?: boolean;
}

export interface EnrollmentPatientInput {
  givenName?: string | null;
  familyName?: string | null;
  birthDate?: string | null;
  sex?: string | null;
  phones?: Array<string | null | undefined>;
  address?: Partial<EnrollmentAddress> | null;
  clinicId?: string | null;
  clinicName?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  practitionerId?: string | null;
  practitionerName?: string | null;
  mrn2?: string | null;
  /** Codes on the patient's own diagnosis list. */
  diagnosisCodes?: string[];
  /**
   * Whether one of those codes is marked primary. Undefined means the caller did not say
   * (two codes is enough). `false` means none is primary.
   */
  diagnosisHasPrimary?: boolean;
  /** The generic/device-default code on the patient (`diagnosisOption` / `icdDiagnosis`). */
  defaultDiagnosisCode?: string | null;
  oac?: string | null;
  isHfMonitored?: boolean | null;
  /** Chart flag. Insurance is required when this is true on a split-bill clinic. */
  isRemoteMonitored?: boolean | null;
}

export interface EnrollmentChecklistInputs {
  patient: EnrollmentPatientInput;
  /** The device this enrollment is for. `null` when the patient has none. */
  device?: EnrollmentDeviceInput | null;
  insurance?: {
    primary?: { payerName?: string | null; memberId?: string | null } | null;
    /** Patient `financialPayerOverride` (P100, SELF-PAY, …). Shown and editable; optional. */
    financialOverride?: string | null;
  } | null;
  enrollment?: Partial<EnrollmentRecord> | null;
  account: {
    /** Parent account billing type first, else the clinic's. */
    billingType?: string | null;
    /** Parent account `ehrIntegration` first, else the clinic's. */
    ehrIntegrated?: boolean | null;
    /**
     * Account bills with multiple diagnosis codes. One code still completes enrollment, including
     * the generic/device-default code. Two or more need one marked primary. When the clinic has a
     * parent, only the parent's `enableMultipleDiagnosesBilling` counts.
     */
    multipleDiagnoses?: boolean | null;
    /** The clinic has departments to choose from. */
    departmentsAvailable?: boolean | null;
    /**
     * The clinic's only department. It satisfies the department row without writing the patient.
     * A picker (and a save) is only used when there is more than one.
     */
    soleDepartment?: { id: string; name: string } | null;
    /** Clinic scheduling setting `isHfMonitoringEnabled`. Off unless the clinic turned it on. */
    hfMonitoringEnabled?: boolean | null;
    /** Vendors the clinic monitors for heart failure. Null uses the standard vendor list. */
    hfManufacturers?: string[] | null;
  };
  settings?: {
    enrollmentChecklist?: EnrollmentChecklistToggles | null;
    /** Device types billed fee for service on Hybrid accounts. */
    ffsDeviceTypes?: string[] | null;
  } | null;
  /**
   * Require "Address verified" (PostGrid or bypass). Off for now: the business deferred
   * address verification. Defaults to ADDRESS_VERIFICATION_REQUIRED.
   */
  requireAddressVerification?: boolean;
}

/** Flip to true to bring back the "Address verified" checklist item and its buttons. */
export const ADDRESS_VERIFICATION_REQUIRED = false;

export const CHECKLIST_SECTION_LABEL: Record<ChecklistSectionKey, string> = {
  demographics: 'Patient',
  clinic: 'Clinic',
  insurance: 'Insurance',
  device: 'Device',
  clinical: 'Clinical',
  vendor: 'Vendor site'
};

export const TOGGLE_OFF_REASON = 'Turned off for this account';

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function has(value: unknown): boolean {
  return text(value).length > 0;
}

/**
 * A toggle is on only when the clinic or account explicitly checked it (`true`), the same rule
 * the ops portal uses. A missing key is off.
 */
export function isToggleOn(
  toggles: EnrollmentChecklistToggles | null | undefined,
  key: keyof EnrollmentChecklistToggles
): boolean {
  return toggles?.[key] === true;
}

export function normalizeBillingType(raw?: string | null): AccountBillingType | null {
  const key = text(raw)
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  if (key === 'splitbill' || key === 'split') return 'Split Bill';
  if (key === 'feeforservice' || key === 'ffs') return 'Fee For Service';
  if (key === 'hybrid') return 'Hybrid';
  return null;
}

/** `crt-d`, `CRT D`, `crt_d` → `CRT-D`. */
export function normalizeDeviceType(raw?: string | null): string {
  return text(raw)
    .toUpperCase()
    .replace(/[\s_]+/g, '-');
}

/**
 * Split Bill → insurance required. Fee For Service → not required. Hybrid → Split Bill unless
 * the device type is billed fee for service on this account.
 */
export function effectiveBilling(
  billingType: string | null | undefined,
  deviceType: string | null | undefined,
  ffsDeviceTypes: string[] | null | undefined
): { splitBill: boolean; reason: string } {
  const kind = normalizeBillingType(billingType);
  if (kind === 'Fee For Service') return { splitBill: false, reason: 'Fee for service account' };
  if (kind === 'Hybrid') {
    const type = normalizeDeviceType(deviceType);
    if (!type) {
      return { splitBill: true, reason: 'Hybrid account — no device yet, so billed split bill' };
    }
    const ffs = new Set((ffsDeviceTypes ?? []).map((item) => normalizeDeviceType(item)));
    if (ffs.has(type)) {
      return { splitBill: false, reason: `Hybrid account — ${type} billed fee for service` };
    }
    return { splitBill: true, reason: `Hybrid account — ${type} billed split bill` };
  }
  if (kind === 'Split Bill') return { splitBill: true, reason: 'Split Bill account' };
  return { splitBill: true, reason: 'Billing type not set — treated as Split Bill' };
}

function phoneDigits(value?: string | null): string {
  let digits = text(value).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits;
}

export function hasUsablePhone(phones?: Array<string | null | undefined>): boolean {
  return (phones ?? []).some((phone) => phoneDigits(phone).length >= 10);
}

/** Street, city, a real US state and a well-formed ZIP (same rules as the edit form). */
export function isAddressComplete(address?: Partial<EnrollmentAddress> | null): boolean {
  return isValidUsAddress(address);
}

/** What's wrong with the address, in one line, or undefined when it's valid. */
export function addressProblem(address?: Partial<EnrollmentAddress> | null): string | undefined {
  const errors = validateAddress(address, { required: true });
  const order = ['line1', 'city', 'state', 'postalCode', 'line2'] as const;
  const messages = order.map((field) => errors[field]).filter(Boolean);
  return messages.length ? messages.join(' ') : undefined;
}

function addressPart(value?: string | null): string {
  return text(value).toUpperCase().replace(/[.,#]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Same address, ignoring case, punctuation, spacing, and a ZIP+4 suffix. */
export function sameAddress(
  a?: Partial<EnrollmentAddress> | null,
  b?: Partial<EnrollmentAddress> | null
): boolean {
  if (!a || !b) return false;
  const zip = (value?: string | null) => text(value).replace(/\D/g, '').slice(0, 5);
  return (
    addressPart(a.line1) === addressPart(b.line1) &&
    addressPart(a.line2) === addressPart(b.line2) &&
    addressPart(a.city) === addressPart(b.city) &&
    addressPart(a.state) === addressPart(b.state) &&
    zip(a.postalCode) === zip(b.postalCode)
  );
}

export function formatEnrollmentAddress(address?: Partial<EnrollmentAddress> | null): string {
  if (!address) return '';
  const street = [text(address.line1), text(address.line2)].filter(Boolean).join(' ');
  const cityState = [text(address.city), text(address.state)].filter(Boolean).join(', ');
  return [street, [cityState, text(address.postalCode)].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}

function item(
  key: string,
  label: string,
  required: boolean,
  complete: boolean,
  extra: { reason?: string; value?: string } = {}
): ChecklistItem {
  const result: ChecklistItem = { key, label, required, complete };
  if (extra.reason) result.reason = extra.reason;
  if (extra.value) result.value = extra.value;
  return result;
}

function section(
  key: ChecklistSectionKey,
  items: ChecklistItem[],
  notRequiredReason?: string
): ChecklistSection {
  const required = items.filter((row) => row.required);
  const missingCount = required.filter((row) => !row.complete).length;
  if (required.length === 0) {
    return {
      key,
      label: CHECKLIST_SECTION_LABEL[key],
      status: 'not_required',
      reason: notRequiredReason || 'Nothing required for this account',
      missingCount: 0,
      items
    };
  }
  return {
    key,
    label: CHECKLIST_SECTION_LABEL[key],
    status: missingCount === 0 ? 'complete' : 'incomplete',
    missingCount,
    items
  };
}

function demographicsSection(inputs: EnrollmentChecklistInputs): ChecklistSection {
  const { patient, enrollment } = inputs;
  const on = isToggleOn(inputs.settings?.enrollmentChecklist, 'demographics');
  const off = on ? undefined : TOGGLE_OFF_REASON;
  const addressComplete = isAddressComplete(patient.address);
  const verification = enrollment?.addressVerification ?? null;
  const verificationStale = Boolean(
    verification?.address && addressComplete && !sameAddress(verification.address, patient.address)
  );
  const addressVerified = Boolean(
    addressComplete &&
      verification &&
      (verification.status === 'verified' || verification.status === 'bypassed') &&
      !verificationStale
  );
  let verifiedReason = 'Verify the address with PostGrid, or bypass it with a reason';
  if (!addressComplete) verifiedReason = 'Add a complete address, then verify it';
  else if (verificationStale) verifiedReason = 'The address changed after it was verified';
  let verifiedValue: string | undefined;
  if (addressVerified && verification?.status === 'verified') verifiedValue = 'Verified';
  if (addressVerified && verification?.status === 'bypassed') {
    verifiedValue = `Bypassed${text(verification.reason) ? `: ${text(verification.reason)}` : ''}`;
  }
  const name = (value?: string | null) => (has(value) ? text(value) : undefined);
  const sex = recordedPatientSex(patient.sex);
  const ehr = Boolean(inputs.account.ehrIntegrated);
  const fields = on
    ? [
        item('firstName', 'First name', true, has(patient.givenName), {
          reason: 'Needed to identify the patient',
          value: name(patient.givenName)
        }),
        item('lastName', 'Last name', true, has(patient.familyName), {
          reason: 'Needed to identify the patient',
          value: name(patient.familyName)
        }),
        item('birthDate', 'Date of birth', true, has(patient.birthDate), {
          reason: 'Needed to match the patient on the vendor site and with payers',
          value: name(patient.birthDate)
        }),
        item('sex', 'Sex', true, sex !== undefined, {
          reason: 'Needed for eligibility checks',
          value: sex
        }),
        item('phone', 'Phone', true, hasUsablePhone(patient.phones), {
          reason: 'At least one 10-digit phone number is needed for outreach',
          value: (patient.phones ?? []).map(text).filter(Boolean).join(' · ') || undefined
        }),
        item('address', 'Address', true, addressComplete, {
          reason: hasAnyAddressPart(patient.address)
            ? addressProblem(patient.address)
            : 'Street, city, state, and ZIP are needed for letters and eligibility',
          value: formatEnrollmentAddress(patient.address) || undefined
        })
      ]
    : [];
  return section(
    'demographics',
    [
      ...fields,
      ...(ehr
        ? [
            item('mrn', 'MRN', true, has(patient.mrn2), {
              reason: 'This account is EHR integrated, so reports need the MRN',
              value: has(patient.mrn2) ? text(patient.mrn2) : undefined
            })
          ]
        : []),
      ...((inputs.requireAddressVerification ?? ADDRESS_VERIFICATION_REQUIRED)
        ? [
            item('addressVerified', 'Address verified', true, addressVerified, {
              reason: addressVerified ? undefined : verifiedReason,
              value: verifiedValue
            })
          ]
        : [])
    ],
    off
  );
}

function clinicSection(inputs: EnrollmentChecklistInputs): ChecklistSection {
  const { patient, account } = inputs;
  const toggles = inputs.settings?.enrollmentChecklist;
  const departmentOn = isToggleOn(toggles, 'department');
  const needsDepartment = Boolean(account.departmentsAvailable);
  const sole = !has(patient.departmentId) ? account.soleDepartment : null;
  const departmentComplete =
    has(patient.clinicId) && (!needsDepartment || has(patient.departmentId) || Boolean(sole?.id));
  const departmentValue =
    [text(patient.clinicName), text(patient.departmentName) || text(sole?.name)]
      .filter(Boolean)
      .join(' · ') || undefined;
  return section(
    'clinic',
    [
      ...(departmentOn
        ? [
            item(
              'department',
              needsDepartment ? 'Clinic / department' : 'Clinic',
              true,
              departmentComplete,
              {
                reason: needsDepartment
                  ? 'Pick the department this patient is followed in'
                  : 'The patient must belong to a clinic',
                value: departmentValue
              }
            )
          ]
        : []),
      item('practitioner', 'Following practitioner', true, has(patient.practitionerId), {
        reason: 'Reports route to the following practitioner',
        value: has(patient.practitionerName) ? text(patient.practitionerName) : undefined
      })
    ],
    'Turned off for this account'
  );
}

function insuranceSection(
  inputs: EnrollmentChecklistInputs,
  billing: { splitBill: boolean; reason: string }
): ChecklistSection {
  const on = isToggleOn(inputs.settings?.enrollmentChecklist, 'insurance');
  const remoteMonitored = inputs.patient.isRemoteMonitored === true;
  const required = billing.splitBill && (on || remoteMonitored);
  const notRequiredReason = !billing.splitBill ? billing.reason : TOGGLE_OFF_REASON;
  const primary = inputs.insurance?.primary ?? null;
  // An active plan ranked Primary counts as verified (no separate eligibility step for now).
  const hasPrimary = Boolean(primary && has(primary.payerName));
  const primaryValue =
    primary && hasPrimary
      ? [text(primary.payerName), text(primary.memberId)].filter(Boolean).join(' · ')
      : undefined;
  return section(
    'insurance',
    required
      ? [
          item('primaryInsurance', 'Primary insurance', true, hasPrimary, {
            reason: remoteMonitored
              ? 'Remote monitored on a split bill clinic, so an active primary insurance is needed'
              : `${billing.reason}, so an active primary insurance is needed`,
            value: primaryValue ? `${primaryValue} · Verified (active primary)` : undefined
          })
        ]
      : [],
    notRequiredReason
  );
}

function deviceSummary(device: EnrollmentDeviceInput, inactive: boolean): string {
  return [
    text(device.vendorName) || text(device.vendor),
    normalizeDeviceType(device.type),
    inactive ? 'Inactive' : null
  ]
    .filter(Boolean)
    .join(' · ');
}

function deviceSection(inputs: EnrollmentChecklistInputs): ChecklistSection {
  const on = isToggleOn(inputs.settings?.enrollmentChecklist, 'deviceAdded');
  const device = inputs.device ?? null;
  if (!on) return section('device', [], TOGGLE_OFF_REASON);
  const active = Boolean(device) && device?.active !== false;
  const inactive = Boolean(device) && device?.active === false;
  return section('device', [
    item('device', 'Device', true, active, {
      reason: inactive
        ? 'The linked device is inactive.'
        : 'Add the device this patient is enrolling with',
      value: device ? deviceSummary(device, inactive) || 'Device on file' : undefined
    })
  ]);
}

const DEFAULT_HF_MANUFACTURERS = ['abbott', 'biotronik', 'boston_scientific', 'medtronic', 'sorin'];

function vendorKey(value?: string | null): string {
  return text(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

/** HF monitoring is the patient setting, shown only when the clinic, device, and vendor allow it. */
export function heartFailureMonitoringApplies(input: {
  enabled?: boolean | null;
  deviceType?: string | null;
  vendor?: string | null;
  manufacturers?: string[] | null;
  deviceActive?: boolean;
}): boolean {
  if (input.enabled !== true || input.deviceActive === false) return false;
  const type = normalizeDeviceType(input.deviceType);
  if (!type || type === 'ILR') return false;
  const vendor = vendorKey(input.vendor);
  if (!vendor) return false;
  const allowed = (input.manufacturers ?? DEFAULT_HF_MANUFACTURERS).map((name) => vendorKey(name));
  return allowed.includes(vendor);
}

/** Shown on the patient list when a multiple-diagnosis account has no codes at all. */
export const MISSING_DIAGNOSIS_REASON = 'Missing diagnosis code';

function diagnosisRequirement(
  multi: boolean,
  count: number,
  hasPrimary: boolean | undefined,
  fallback: string
): { complete: boolean; reason: string } {
  const hasOne = count > 0 || Boolean(fallback);
  if (!hasOne) {
    return {
      complete: false,
      reason: multi
        ? MISSING_DIAGNOSIS_REASON
        : 'A patient diagnosis or the device default code is needed to bill'
    };
  }
  // One code from the order is enough, including a generic or device-default code.
  // Two or more still need one marked primary.
  if (multi && count >= 2 && hasPrimary === false) {
    return { complete: false, reason: 'One diagnosis must be marked primary' };
  }
  return { complete: true, reason: '' };
}

function clinicalSection(inputs: EnrollmentChecklistInputs): ChecklistSection {
  const { patient } = inputs;
  const on = isToggleOn(inputs.settings?.enrollmentChecklist, 'diagnosis');
  const multi = inputs.account.multipleDiagnoses === true;
  const codes = (patient.diagnosisCodes ?? []).map(text).filter(Boolean);
  const fallback = text(patient.defaultDiagnosisCode);
  const diagnosisValue = codes.length
    ? codes.join(', ')
    : fallback
      ? `${fallback} (device default)`
      : undefined;
  const diagnosis = diagnosisRequirement(
    multi,
    codes.length,
    patient.diagnosisHasPrimary,
    fallback
  );
  const hf = patient.isHfMonitored;
  const showHf = heartFailureMonitoringApplies({
    enabled: inputs.account.hfMonitoringEnabled,
    deviceType: inputs.device?.type,
    vendor: inputs.device?.vendor,
    manufacturers: inputs.account.hfManufacturers,
    deviceActive: inputs.device ? inputs.device.active !== false : false
  });
  return section(
    'clinical',
    [
      ...(on || multi
        ? [
            item('diagnosis', 'Diagnosis code', true, diagnosis.complete, {
              reason: diagnosis.reason,
              value: diagnosisValue
            })
          ]
        : []),
      item('oac', 'OAC status', false, has(patient.oac), {
        reason: 'Optional',
        value: has(patient.oac) ? text(patient.oac) : undefined
      }),
      ...(showHf
        ? [
            item('hfMonitoring', 'HF monitoring', false, typeof hf === 'boolean', {
              reason: 'Optional',
              value: typeof hf === 'boolean' ? (hf ? 'Yes' : 'No') : undefined
            })
          ]
        : [])
    ],
    TOGGLE_OFF_REASON
  );
}

export function transmissionAvailability(
  device: EnrollmentDeviceInput | null | undefined,
  enrollment: Partial<EnrollmentRecord> | null | undefined
): { value: boolean | null; source: 'auto' | 'admin' | null } {
  if (device && device.active !== false && has(device.lastTransmissionAt)) {
    return { value: true, source: 'auto' };
  }
  const stored = enrollment?.transmissionAvailable;
  if (stored && typeof stored.value === 'boolean') {
    return { value: stored.value, source: stored.source === 'auto' ? 'auto' : 'admin' };
  }
  return { value: null, source: null };
}

function vendorSection(
  inputs: EnrollmentChecklistInputs,
  availability: { value: boolean | null; source: 'auto' | 'admin' | null }
): ChecklistSection {
  let value: string | undefined;
  if (availability.value === true) {
    value = availability.source === 'auto' ? 'Yes (transmission received)' : 'Yes';
  } else if (availability.value === false) {
    value = 'No — a new monitor setup task is created on completion';
  }
  return section('vendor', [
    item('transmissionAvailable', 'Transmission available', true, availability.value !== null, {
      reason:
        availability.value === null
          ? 'Check the vendor site and choose Yes or No'
          : inputs.device && has(inputs.device.lastTransmissionAt)
            ? 'Set from the device’s last transmission'
            : undefined,
      value
    })
  ]);
}

export function evaluateEnrollmentChecklist(
  inputs: EnrollmentChecklistInputs
): EnrollmentChecklistResult {
  const billing = effectiveBilling(
    inputs.account.billingType,
    inputs.device?.type,
    inputs.settings?.ffsDeviceTypes
  );
  const availability = transmissionAvailability(inputs.device, inputs.enrollment);
  const sections = [
    demographicsSection(inputs),
    clinicSection(inputs),
    insuranceSection(inputs, billing),
    deviceSection(inputs),
    clinicalSection(inputs),
    vendorSection(inputs, availability)
  ];
  const noDevice = !inputs.device;
  const missing: ChecklistMissing[] = [];
  sections.forEach((row) => {
    row.items.forEach((entry) => {
      if (!entry.required || entry.complete) return;
      if (row.key === 'device' && noDevice && entry.key !== 'device') return;
      missing.push({ sectionKey: row.key, itemKey: entry.key, label: entry.label });
    });
  });
  const required = sections.filter((row) => row.status !== 'not_required');
  return {
    sections,
    missing,
    isComplete: missing.length === 0,
    completeSections: required.filter((row) => row.status === 'complete').length,
    requiredSections: required.length,
    transmissionAvailable: availability,
    billing
  };
}
